import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DohopHeader from "../../components/dohop/DohopHeader";
import { DohopOffer, DohopDataLists } from "../../types/dohop";
import { formatDuration, formatTime } from "../../services/dohopApi";
import { MovmoCheckoutButton, consumeMovmoCheckoutReturn } from "@movmo/express-checkout-button";
import "@movmo/express-checkout-button/dist/index.css";
import { queryWithoutMovmo } from "../../utils/preserveQuery";

const DohopCheckout: React.FC = () => {
    const navigate = useNavigate();
    const [offer, setOffer] = useState<DohopOffer | null>(null);
    const [dataLists, setDataLists] = useState<DohopDataLists | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [bookingConfirmed, setBookingConfirmed] = useState(false);

    // MOVMO-549: Handle page-level return from top-level redirect.
    useEffect(() => {
        consumeMovmoCheckoutReturn({
            isProd: false,
            onBookingComplete: () => {
                console.log("[dohop-checkout] booking complete");
            },
            onExit: ({ reason }: { reason: string }) => {
                console.log("[dohop-checkout] movmo checkout exit:", reason);
                if (reason === "booking_confirmed") {
                    setBookingConfirmed(true);
                } else if (reason !== undefined) {
                    navigate("/dohop" + queryWithoutMovmo(window.location.search));
                }
            },
        });
    }, []);

    useEffect(() => {
        const storedOffer = sessionStorage.getItem("selectedDohopOffer");
        const storedDataLists = sessionStorage.getItem("dohopDataLists");

        if (storedOffer && storedDataLists) {
            try {
                setOffer(JSON.parse(storedOffer));
                setDataLists(JSON.parse(storedDataLists));
            } catch (error) {
                console.error("Error parsing stored offer:", error);
                navigate("/dohop", { replace: true });
            }
        } else if (!bookingConfirmed) {
            navigate("/dohop", { replace: true });
        }

        setIsLoading(false);
    }, [navigate, bookingConfirmed]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-dohop-gray">
                <DohopHeader />
                <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-dohop-blue border-t-transparent"></div>
                </div>
            </div>
        );
    }

    // Allow rendering if we have offer/dataLists OR if we're showing a booking confirmation
    if (!offer || !dataLists) {
        if (!bookingConfirmed) {
            return null;
        }
        // Render minimal confirmation page
        return (
            <div className="min-h-screen bg-dohop-gray">
                <DohopHeader />
                <div className="flex items-center justify-center py-16 px-4">
                    <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
                        <div className="text-2xl font-semibold text-green-600 mb-2">
                            Booking confirmed with Movmo
                        </div>
                        <p className="text-gray-600 mb-6">Your booking has been successfully confirmed.</p>
                        <button
                            onClick={() => navigate("/dohop")}
                            className="bg-dohop-blue text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                        >
                            Return to Search
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Get journey and segment details
    const journeyIds = offer.offer_items[0]?.services[0]?.journey_ids || [];
    const journey = dataLists.journeys.find((j) =>
        journeyIds.includes(j.journey_id)
    );
    const segments = journey
        ? journey.segment_ids
              .map((segId) =>
                  dataLists.segments.find((s) => s.segment_id === segId)
              )
              .filter(Boolean)
        : [];

    const firstSegment = segments[0];
    const lastSegment = segments[segments.length - 1];

    const totalDuration = segments.reduce(
        (acc, seg) => acc + (seg?.duration || 0),
        0
    );

    const stopsCount = segments.length - 1;
    const stopsText =
        stopsCount === 0
            ? "Direct"
            : `${stopsCount} stop${stopsCount > 1 ? "s" : ""}`;

    const handleBackToResults = () => {
        navigate(-1);
    };

    return (
        <div className="min-h-screen bg-dohop-gray">
            <DohopHeader />

            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back button */}
                <button
                    onClick={handleBackToResults}
                    className="flex items-center gap-2 text-dohop-blue hover:underline mb-6"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    Back to results
                </button>

                <h1 className="text-2xl font-bold text-dohop-navy mb-6">
                    Review Your Selection
                </h1>

                {/* Flight summary card */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="bg-dohop-blue px-6 py-4">
                        <h2 className="text-lg font-semibold text-white">
                            Flight Details
                        </h2>
                    </div>

                    <div className="p-6">
                        {firstSegment && lastSegment && (
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                                {/* Route */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-4">
                                        {/* Departure */}
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-dohop-navy">
                                                {formatTime(
                                                    firstSegment.departure
                                                        .datetime
                                                )}
                                            </div>
                                            <div className="text-lg text-gray-600">
                                                {
                                                    firstSegment.departure
                                                        .airport_code
                                                }
                                            </div>
                                        </div>

                                        {/* Arrow and duration */}
                                        <div className="flex-1 text-center">
                                            <div className="border-t-2 border-dashed border-gray-300 relative">
                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-3">
                                                    <svg
                                                        className="w-6 h-6 text-dohop-blue"
                                                        fill="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="mt-2 text-sm text-gray-500">
                                                {formatDuration(totalDuration)}{" "}
                                                · {stopsText}
                                            </div>
                                        </div>

                                        {/* Arrival */}
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-dohop-navy">
                                                {formatTime(
                                                    lastSegment.arrival.datetime
                                                )}
                                            </div>
                                            <div className="text-lg text-gray-600">
                                                {
                                                    lastSegment.arrival
                                                        .airport_code
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Segment details */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="font-semibold text-dohop-navy mb-4">
                                Flight Segments
                            </h3>
                            <div className="space-y-4">
                                {segments.map((segment) => {
                                    if (!segment) return null;
                                    return (
                                        <div
                                            key={segment.segment_id}
                                            className="flex items-center gap-4 p-4 bg-dohop-gray rounded-lg"
                                        >
                                            <div className="w-12 h-12 bg-dohop-ice rounded-lg flex items-center justify-center">
                                                <span className="text-dohop-blue font-bold text-sm">
                                                    {
                                                        segment
                                                            .marketing_carrier
                                                            .carrier_code
                                                    }
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium text-dohop-navy">
                                                    {
                                                        segment
                                                            .marketing_carrier
                                                            .carrier_code
                                                    }
                                                    {
                                                        segment
                                                            .marketing_carrier
                                                            .flight_number
                                                    }
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {
                                                        segment.departure
                                                            .airport_code
                                                    }{" "}
                                                    {formatTime(
                                                        segment.departure
                                                            .datetime
                                                    )}{" "}
                                                    →{" "}
                                                    {
                                                        segment.arrival
                                                            .airport_code
                                                    }{" "}
                                                    {formatTime(
                                                        segment.arrival.datetime
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {formatDuration(
                                                    segment.duration
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Price summary */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-semibold text-dohop-navy">
                                    Total Price
                                </h3>
                                <p className="text-sm text-gray-500">
                                    per person, includes taxes and fees
                                </p>
                            </div>
                            <div className="text-3xl font-bold text-dohop-blue">
                                {offer.total_price.currency}{" "}
                                {offer.total_price.amount.toFixed(2)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Dohop Protection notice */}
                <div className="bg-dohop-ice rounded-xl p-6 mb-6">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-dohop-blue rounded-full flex items-center justify-center flex-shrink-0">
                            <svg
                                className="w-5 h-5 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-semibold text-dohop-navy mb-1">
                                Dohop Protection Included
                            </h3>
                            <p className="text-sm text-gray-600">
                                Your connection is guaranteed. If you miss a
                                connecting flight due to a delay, we'll get you
                                to your destination at no extra cost.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Checkout button */}
                <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-semibold text-dohop-navy mb-4 text-center">
                        Complete Your Booking
                    </h3>
                    <div className="flex justify-center">
                        <MovmoCheckoutButton
                            iataCode="FI"
                            apiKey={process.env.REACT_APP_MOVMO_API_KEY || ""}
                            providerID="dohop"
                            flightOffer={offer}
                        />
                    </div>
                    <p className="text-center text-sm text-gray-500 mt-4">
                        Secure checkout powered by Movmo
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DohopCheckout;
